import {
  csx,
  DataView,
  DataViewHeader,
  Flex,
  FlexSpacer,
  Page,
  PageContent,
  PageHeader,
  PageHeaderTitle,
  PageHeaderTop,
  Pagination,
  Search,
  Table,
  TBody,
  TBodyCell,
  TBodyRow,
  THead,
  THeadCell,
  useDataViewState,
  usePaginationState,
  useSearchState,
  useTableState,
} from '@vtex/admin-ui'
import { useCallback, useEffect, useRef, useState } from 'react'
import { defineMessages, FormattedMessage, useIntl } from 'react-intl'

import { useAnalytics } from 'use-analytics'
import { archivedPromotionsListColumns as columns } from './columns'

import { useNavigation } from '@vtex/raccoon-next'
import {
  useArchivedPromotions,
  UseArchivedPromotionsReturn,
  useCache,
} from 'hooks'
import { Event, type ArchivedSimplifiedPromotion } from 'typings'

const ITEMS_PER_PAGE = 30

const messages = defineMessages({
  title: { id: 'admin/promotions.archived.title' },
  searchPlaceholder: { id: 'admin/promotions.list.archived.search' },
})

export function ArchivedPromotionsList() {
  const { track } = useAnalytics()
  const { navigate } = useNavigation()
  const { formatMessage } = useIntl()
  const { get, set } = useCache()
  const pageRef = useRef(false)
  const pageIsReady = pageRef.current

  const [promotions, setPromotions] = useState<ArchivedSimplifiedPromotion[]>(
    []
  )

  const paginationState = usePaginationState({
    pageSize: ITEMS_PER_PAGE,
    initialPage: get('archivedPage', 1),
  })

  const search = useSearchState({
    timeout: 500,
    initialValue: get('archivedSearch', ''),
  })

  const searchTerm = search.debouncedValue

  const viewState = useDataViewState({
    loading: true,
    error: null,
    empty: null,
    notFound: false,
  })

  const activeFiltersMetric = searchTerm ? ['textSearch'] : []

  const handleNavigate = (params: ArchivedSimplifiedPromotion) => {
    const { id, type } = params

    track(Event.NAVIGATED_TO_ARCHIVED_PROMOTION, {
      type,
      page: paginationState.currentPage,
      activeFilters: activeFiltersMetric,
    })

    navigate(`/archived/${id}`)
  }

  const updatePagination = useCallback(
    (data: UseArchivedPromotionsReturn) => {
      const total = data.pagination.total ?? 0

      paginationState.total !== total &&
        paginationState.paginate({
          type: 'setTotal',
          total,
        })

      const isPageValid =
        total > (paginationState.currentPage - 1) * ITEMS_PER_PAGE

      if (!isPageValid) paginationState.paginate({ type: 'reset' })

      set('archivedPage', paginationState.currentPage)
    },
    [set]
  )

  const {
    getTable,
    getBodyCell,
    getHeadCell,
    data: items,
  } = useTableState({
    columns,
    items: promotions,
    status: viewState.status,
    length: ITEMS_PER_PAGE,
  })

  const {
    data,
    isFetching: loading,
    error,
  } = useArchivedPromotions({
    page: paginationState.currentPage,
    pageSize: ITEMS_PER_PAGE,
    query: searchTerm,
  })

  useEffect(() => {
    if (data) {
      updatePagination(data)
    }
  }, [data, updatePagination])

  useEffect(() => {
    const promotionsForPage = data?.items ?? []

    setPromotions(promotionsForPage)

    data !== undefined && (pageRef.current = true)
  }, [data])

  useEffect(() => {
    set('archivedSearch', searchTerm)
  }, [searchTerm, set])

  useEffect(() => {
    const promotionsRetrieved = data?.items ?? []

    if (loading) {
      return viewState.setStatus({ type: 'loading' })
    }

    if (error) {
      return viewState.setStatus({
        type: 'error',
      })
    }

    if (searchTerm && !promotionsRetrieved.length) {
      return viewState.setStatus({
        type: 'not-found',
      })
    }

    if (!promotionsRetrieved.length) {
      return viewState.setStatus({
        type: 'empty',
      })
    }

    viewState.setStatus({ type: 'ready' })
  }, [data, loading, searchTerm, error])

  useEffect(() => {
    pageIsReady &&
      track(Event.ARCHIVED_PROMOTIONS_LISTED, {
        items: data?.pagination.total,
        appliedFilters: activeFiltersMetric,
      })
  }, [pageIsReady])

  useEffect(() => {
    pageIsReady &&
      track(Event.ARCHIVED_SEARCH_PERFORMED, { length: searchTerm.length })
  }, [searchTerm])

  useEffect(() => {
    if (pageIsReady) {
      track(Event.ARCHIVED_PAGE_CHANGED, { page: paginationState.currentPage })

      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
    }
  }, [paginationState.currentPage])

  return (
    <Page>
      <PageHeader onPopNavigation={() => navigate('/')}>
        <PageHeaderTop>
          <PageHeaderTitle>
            <FormattedMessage {...messages.title} />
          </PageHeaderTitle>
        </PageHeaderTop>
      </PageHeader>
      <PageContent layout="wide" className={csx({ display: 'block' })}>
        <DataView state={viewState} className={csx({ paddingTop: '$space-2' })}>
          <DataViewHeader
            className={csx({ paddingTop: '$space-0', display: 'flex' })}
          >
            <Flex direction="column" className={csx({ width: 'full' })}>
              <Flex
                align="center"
                direction="row"
                className={csx({ paddingBottom: '$space-5' })}
              >
                <Search
                  id="archived-promotions-search"
                  placeholder={formatMessage(messages.searchPlaceholder)}
                  className={csx({
                    width: '4/12',
                  })}
                  {...search.getInputProps()}
                />
                <FlexSpacer />
                <Pagination state={paginationState} />
              </Flex>
            </Flex>
          </DataViewHeader>
          <Table
            {...getTable()}
            className={csx({
              width: 'full',
            })}
          >
            <THead>
              {columns.map((column) => (
                // eslint-disable-next-line react/jsx-key
                <THeadCell {...getHeadCell(column)} />
              ))}
            </THead>
            <TBody>
              {items.map((item, index) => (
                <TBodyRow
                  key={index}
                  {...(viewState.status === 'ready' && {
                    onClick: () => handleNavigate(item),
                  })}
                >
                  {columns.map((column) => (
                    // eslint-disable-next-line react/jsx-key
                    <TBodyCell
                      {...getBodyCell(column, item)}
                      className={csx({ height: 70 })}
                    />
                  ))}
                </TBodyRow>
              ))}
            </TBody>
          </Table>
        </DataView>

        {!paginationState.nextDisabled ? (
          <Pagination
            state={paginationState}
            className={csx({ float: 'right', marginY: '$space-5' })}
          />
        ) : null}
      </PageContent>
    </Page>
  )
}
